import INotification, {
  INotificationSeverity,
} from '@scale/shared/types/notifications/notification';
import INotificationSend from '@scale/shared/types/notifications/notificationSend';
import moment from 'moment';
import { create } from 'zustand';

interface NotificationsSlice {
  currentTab: number;
  dismissingAll: boolean;
  notifications: (INotificationSend & { notification: INotification })[];
  latestDate: string | null;
  hasMore: boolean;
  setting: 'unread' | 'all';
  skip: number;
  initialFetch: boolean;
  initialUncollapsedFetch: boolean;
  incrementSkip: (inc: number) => void;
  setDismissingAll: (dismissAll: boolean) => void;
  setTab: (tab: number) => void;
  setSetting: (setting: 'unread' | 'all' | null) => void;
  setNotifications: (
    notifications: (INotificationSend & { notification: INotification })[],
  ) => void;
  dismissAll: (severity: keyof typeof INotificationSeverity) => void;
  setDismissNotification: (notificationId: string) => void;
  toggleInitialFetch: () => void;
  setInitialUncollapsedFetch: (flag: boolean) => void;
  setHasMore: (flag: boolean) => void;
}

export const useNotificationsStore = create<NotificationsSlice>(set => ({
  currentTab: 0,
  dismissingAll: false,
  setting: 'unread',
  skip: 0,
  notifications: [],
  initialFetch: true,
  initialUncollapsedFetch: true,
  hasMore: true,
  latestDate: null,
  dismissAll: (severity: keyof typeof INotificationSeverity) => {
    set(state => ({
      notifications: state.notifications.map(n =>
        n.notification.severity !== severity && !n.dismissedAt
          ? n
          : { ...n, dismissedAt: new Date() },
      ),
    }));
  },
  setDismissingAll: (flag: boolean) => set({ dismissingAll: flag }),
  setTab: (currentTab: number) => {
    set(() => ({ currentTab }));
  },
  setSetting: (setting: ('unread' | 'all') | null) => {
    if (!setting) return;
    set(() => ({ setting }));
  },
  incrementSkip: (inc: number) => {
    set(state => ({ skip: state.skip + inc }));
  },
  setNotifications: (notifications: (INotificationSend & { notification: INotification })[]) => {
    set(state => {
      const allNotifs = [...state.notifications, ...notifications];
      const sortedNotifications = allNotifs.sort(
        (a, b) => moment(b.createdAt).unix() - moment(a.createdAt).unix(),
      );
      const latestDate =
        sortedNotifications.length > 0 ? sortedNotifications[0].createdAt.toString() : null;

      return {
        latestDate,
        notifications: allNotifs,
      };
    });
  },
  setDismissNotification: (notificationId: string) => {
    set(state => {
      // maybe use a hashmap instead of an array for constant lookup time ? I think it's fine for now
      const notifications = state.notifications.map(n => {
        if (n._id === notificationId) {
          return {
            ...n,
            dismissedAt: new Date(),
          };
        }
        return n;
      });
      return {
        notifications,
      };
    });
  },
  toggleInitialFetch: () => {
    set(state => ({
      initialFetch: !state.initialFetch,
    }));
  },
  setInitialUncollapsedFetch: (flag: boolean) => {
    set(() => ({
      initialUncollapsedFetch: flag,
    }));
  },
  setHasMore: (hasMore: boolean) => {
    set(() => ({
      hasMore,
    }));
  },
}));
